import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { CONTACT_EMAIL_TYPES, CONTACT_NUMBER_TYPES } from '@shared/constants';
import {
  IContactAddress,
  IContactEmail,
  IContactNumber,
  IName,
} from '@shared/interfaces';
import { emailValidator } from '@shared/utils';

import { noWhitespaceValidator } from '../../../core/helpers';
import { IPhoneNumberInputChange } from '../../../shared/components/phone-number-input/types';

export type IAddressFormGroup = FormGroup<{
  is_primary: FormControl<boolean>;
  address: FormGroup<{
    line1: FormControl<string>;
    line2: FormControl<string>;
    line3: FormControl<string>;
    country: FormControl<string>;
    state: FormControl<string>;
    district: FormControl<string>;
    city: FormControl<string>;
    postal_code: FormControl<string>;
  }>;
}>;

export type IEmailFormGroup = FormGroup<{
  is_primary: FormControl<boolean>;
  type: FormControl<CONTACT_EMAIL_TYPES>;
  email: FormControl<string>;
}>;

export type IPhoneFormGroup = FormGroup<{
  is_primary: FormControl<boolean>;
  type: FormControl<CONTACT_NUMBER_TYPES>;
  phone_number: FormControl<IPhoneNumberInputChange>;
}>;

export type INameFormGroup = FormGroup<{
  salutation: FormControl<string>;
  first_name: FormControl<string>;
  last_name: FormControl<string>;
  middle_name: FormControl<string>;
}>;

export type IMultiFieldConfig =
  | { type: 'NAME'; form: INameFormGroup }
  | { type: 'ADDRESS'; form: FormArray<IAddressFormGroup> }
  | { type: 'PHONE'; form: FormArray<IPhoneFormGroup> }
  | { type: 'EMAIL'; form: FormArray<IEmailFormGroup> };

export const generateAddressFormGroup = (
  address?: IContactAddress,
  isPrimary = false
): IAddressFormGroup => {
  return new FormGroup({
    is_primary: new FormControl(
      isPrimary ? true : address?.is_primary ? true : false
    ),
    address: new FormGroup({
      line1: new FormControl(address?.address?.line1, [
        Validators.required,
        noWhitespaceValidator,
      ]),
      line2: new FormControl(address?.address?.line2),
      line3: new FormControl(address?.address?.line3),
      country: new FormControl(address?.address?.country, [
        Validators.required,
      ]),
      state: new FormControl(address?.address?.state, [Validators.required]),
      district: new FormControl(address?.address?.district, [
        Validators.required,
      ]),
      city: new FormControl(address?.address?.city, [Validators.required]),
      postal_code: new FormControl(
        {
          value: address?.address?.postal_code,
          disabled: true,
        },
        [Validators.required]
      ),
    }),
  });
};

export const emailFormGroupValidator = (isRequired = false): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const typeCtrl = group.get('type');
    const emailCtrl = group.get('email');
    if (!typeCtrl || !emailCtrl) return null;

    const typeValue = typeCtrl.value;
    const emailValue = emailCtrl.value;

    // Check if email has value but type is missing -> type should be mandatory
    if (emailValue && !typeValue) {
      if (!typeCtrl.hasError('required')) {
        typeCtrl.setErrors({ ...typeCtrl.errors, required: true });
      }
    } else {
      // Clear the required error on type if email is empty or type has value
      if (typeCtrl.hasError('required')) {
        const errors = { ...typeCtrl.errors };
        delete errors['required'];
        typeCtrl.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
    }

    // Check if isRequired is true but email is missing -> email should be mandatory
    if (isRequired && !emailValue) {
      if (!emailCtrl.hasError('required')) {
        emailCtrl.setErrors({ ...emailCtrl.errors, required: true });
      }
    } else {
      // Clear required error on email if not required or has value
      if (emailCtrl.hasError('required')) {
        const errors = { ...emailCtrl.errors };
        delete errors['required'];
        emailCtrl.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
    }

    return null;
  };
};

export const generateEmailFormGroup = (
  email?: IContactEmail,
  isRequired = false
): IEmailFormGroup => {
  const emailValidators = [emailValidator()];
  if (isRequired) {
    emailValidators.push(Validators.required);
  }
  const group = new FormGroup({
    is_primary: new FormControl(email?.is_primary || false),
    type: new FormControl(email?.type),
    email: new FormControl(email?.email || '', emailValidators),
  });
  group.addValidators(emailFormGroupValidator(isRequired));
  return group;
};

export const generatePhoneFormGroup = (
  number?: IContactNumber,
  is_primary = false,
  primary_disabled = false
): IPhoneFormGroup => {
  return new FormGroup({
    is_primary: new FormControl({
      value: is_primary ? true : number?.is_primary || false,
      disabled: primary_disabled,
    }),
    type: new FormControl(number?.type, [Validators.required]),
    phone_number: new FormControl(
      {
        ...number?.phone_number,
        valid: !!number?.phone_number,
      },
      [Validators.required]
    ),
  });
};

export const generateNameFormGroup = (name?: IName): INameFormGroup => {
  return new FormGroup({
    salutation: new FormControl(name?.salutation || '', [Validators.required]),
    first_name: new FormControl(name?.first_name || '', [Validators.required]),
    last_name: new FormControl(name?.last_name || '', [Validators.required]),
    middle_name: new FormControl(name?.middle_name || ''),
  });
};

export const getAddressDataFromForm = (
  form: FormArray<IAddressFormGroup>
): IContactAddress[] => {
  const controls = form.controls;
  const hasPrimary = controls.some((e) => e.getRawValue().is_primary === true);
  return controls.map((e, index) => {
    const raw = e.getRawValue();
    let is_primary = false;
    if (hasPrimary) {
      is_primary = raw.is_primary === true;
    } else {
      is_primary = index === 0;
    }
    return {
      ...raw,
      is_primary,
    };
  });
};


//Athiq Created - for the pop up removal validation
//start
export const nameForStorage = (form: INameFormGroup): IName | null => {
  const raw = form.getRawValue();
  const hasValue =
    !!raw?.salutation ||
    !!raw?.first_name ||
    !!raw?.last_name ||
    !!raw?.middle_name;
  return hasValue ? (raw as IName) : null;
};


export const emailsForStorage = (
  form: FormArray<IEmailFormGroup>
): IContactEmail[] => {
  return (form.getRawValue() as IContactEmail[]).filter(
    (email) => !!email?.type || !!email?.email
  );
};

export const phonesForStorage = (
  form: FormArray<IPhoneFormGroup>
): IContactNumber[] => {
  return (form.getRawValue() as IContactNumber[]).filter((number) => {
    const phone = number?.phone_number as
      | { number?: string; e164Number?: string; nationalNumber?: string }
      | undefined;
    return (
      !!number?.type ||
      !!phone?.number ||
      !!phone?.e164Number ||
      !!phone?.nationalNumber
    );
  });
};

export const emailArrayCompleteValidator = (
  isRequired = false
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const emails: IContactEmail[] = control.value || [];
    if (!emails.length) return isRequired ? { required: true } : null;
    const allComplete = emails.every((email) => !!email?.type && !!email?.email);
    return allComplete ? null : { incompleteEmail: true };
  };
};
//end
